import React, { useMemo, useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import { App, Button, Card, Space, Tooltip, Typography, Flex } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { Container } from '../../components/CssFrameworkComponents';
import requireAuth from '../../components/hocs/requireAuth';
import PrevNextPagination, { usePageSearchParam } from '../../components/PrevNextPagination';
import { Alert as ErrorAlert } from '../../components/CssFrameworkComponents';
import { usePrerenderUser } from '../../hooks/usePrerenderUser';
import AdminTemplate from '../../layout/AdminTemplate';
import { useEvent } from '../events/hooks/useEvent';
import { UpgradePlanProvider } from '../UpgradePlan';

import {
  useAddSitemapMutation,
  useReCacheSitemapMutation,
  useGetSitemapsQuery,
  useEditSitemapMutation,
  useToggleAllSitemapsMutation,
  useDeleteSitemapMutation,
} from './api/sitemapApiSlice';
import ImportSitemapModal from './components/ImportSitemapModal';
import SitemapsListTable from './components/SitemapsListTable';
import ButtonWithRole from '../../components/ButtonWithRole';
import USER_ROLES from '../auth/userRoles';
import { BannerWithLink } from '~features/gsc-integrations';
import { getSitemapOrigin, SITEMAP_ORIGIN_LABELS } from './sitemapUtil';

const META_TAGS = {
  title: 'Sitemaps - Prerender.io',
};
const { Text, Title } = Typography;

function SitemapListPage() {
  const { message } = App.useApp();
  const [page, setPage] = usePageSearchParam();

  // Load sitemaps
  const {
    data: getSitemapsData,
    currentData: currentSitemapsData,
    isFetching,
    isError,
    refetch,
  } = useGetSitemapsQuery(page, { refetchOnMountOrArgChange: true });

  // Add new hook
  const [
    addNewSitemap,
    { isLoading: isAddNewSitemapLoading, isError: isAddNewSitemapError, reset: resetNewSitemapError },
  ] = useAddSitemapMutation();

  const [_reCacheSitemap, { isError: isReCacheSitemapError, reset: resetRecacheError }] = useReCacheSitemapMutation();

  // Edit hook
  const [editSitemap, { isLoading: isUpdatingSitemap }] = useEditSitemapMutation();

  // Toggle all hook
  const [toggleAllSitemaps, { isLoading: isToggleAllSitemapsLoading }] = useToggleAllSitemapsMutation();

  // Delete sitemap hook
  const [deleteSitemap, { isLoading: isDeleteSitemapLoading }] = useDeleteSitemapMutation();

  const [isImportModalVisible, setIsImportModalVisible] = useState(false);

  const prerenderUser = usePrerenderUser();
  const { track } = useEvent();

  // Reset mutation after closing modal
  useEffect(() => {
    if (!isImportModalVisible) {
      resetNewSitemapError();
      resetRecacheError();
    }
  }, [isImportModalVisible]);

  // Delete sitemap
  const onDeleteSitemap = async (id) => {
    try {
      await deleteSitemap(id).unwrap();
      track('Sitemap List Engaged', {
        subscription_plan: prerenderUser.plan.name,
        smap_is_activated: false,
        smap_is_deactivated: false,
        smap_is_exported: false,
        smap_is_deleted: true,
        smap_num_activated: 0,
        smap_num_deactivated: 0,
      });
    } catch (deleteSitemapError) {
      console.error({ deleteSitemapError });
    }
  };

  // Toggle sitemaps (enabled/disabled)
  const sitemapToggled = async (item) => {
    try {
      await editSitemap({ id: item.id, enabled: !item.enabled }).unwrap();
      track('Sitemap List Engaged', {
        subscription_plan: prerenderUser.plan.name,
        smap_is_activated: item.enabled,
        smap_is_deactivated: !item.enabled,
        smap_is_exported: false,
        smap_is_deleted: false,
        smap_num_activated: item.enabled ? 1 : 0,
        smap_num_deactivated: !item.enabled ? 1 : 0,
      });
    } catch (editSitemapError) {
      console.error({ editSitemapError });
    }
  };

  const deviceTypeUpdated = async (item, adaptiveType) => {
    try {
      await editSitemap({ id: item.id, adaptiveType }).unwrap();
      track('Sitemap List Engaged', {
        subscription_plan: prerenderUser.plan.name,
        smap_is_activated: item.enabled,
        smap_is_deactivated: !item.enabled,
        smap_is_exported: false,
        smap_is_deleted: false,
        smap_num_activated: item.enabled ? 1 : 0,
        smap_num_deactivated: !item.enabled ? 1 : 0,
        smap_adaptive_type: adaptiveType,
      });
      message.success(`Sitemap for ${item.domain} updated successfully`);
    } catch (error) {
      message.error(`An error occured while trying to update the sitemap for ${item.domain}, please try again later.`);
    }
  };

  const revisitIntervalUpdated = async (item, revisitInterval) => {
    try {
      await editSitemap({ id: item.id, revisitInterval }).unwrap();
      track('Sitemap List Engaged', {
        subscription_plan: prerenderUser.plan.name,
        smap_is_activated: item.enabled,
        smap_is_deactivated: !item.enabled,
        smap_is_exported: false,
        smap_is_deleted: false,
        smap_num_activated: item.enabled ? 1 : 0,
        smap_num_deactivated: !item.enabled ? 1 : 0,
        smap_revisit_interval: revisitInterval,
      });
      message.success(`Sitemap for ${item.domain} updated successfully`);
    } catch (error) {
      message.error(`An error occured while trying to update the sitemap for ${item.domain}, please try again later.`);
    }
  };

  const toggleAll = async (newState) => {
    try {
      await toggleAllSitemaps(newState).unwrap();
    } catch (toggleAllSitemapsError) {
      console.error({ toggleAllSitemapsError });
    }
  };

  // Import sitemap modal
  const closeImportModal = () => setIsImportModalVisible(false);

  const onImport = async ({ sitemapUrl, adaptiveType, revisitInterval }) => {
    if (isAddNewSitemapLoading) {
      return;
    }

    const importSpeed = 3600; // TODO: Add to import form

    try {
      await addNewSitemap({
        url: sitemapUrl,
        importSpeed,
        revisitInterval,
        adaptiveType,
      }).unwrap();
      closeImportModal();
      track('Sitemap Imported', {
        subscription_plan: prerenderUser.chargebeePlanId,
      });
    } catch (addNewSitemapError) {
      console.error({ addNewSitemapError });
    }
  };

  // Transform sitemaps from BE for display
  const columns = [
    'Status',
    'Sitemap URL',
    'Created At',
    'Revisit Interval',
    'Device',
    'Last Visited At',
    'Next Visit',
    'Origin',
    'Health',
  ];

  const [visibleItems, csvData] = useMemo(() => {
    const visibleItems = getSitemapsData?.data
      ? [...getSitemapsData.data]
          .sort((a, b) => b.id - a.id)
          .map((item) => {
            const intervalHours = parseInt(item.revisitInterval, 10) / 60 / 60;

            const next_visit = item.lastCheckedAt
              ? new Date(
                  new Date().setTime(new Date(item.lastCheckedAt).getTime() + intervalHours * 60 * 60 * 1000)
                ).toISOString()
              : null;

            const health = item.isHealthy === null ? 'Unknown' : item.isHealthy === true ? 'Healthy' : 'Unhealthy';
            return {
              id: item.id,
              enabled: item.isEnabled,
              sitemap_url: item.url,
              created_at: item.createdAt,
              interval: item.revisitInterval,
              device_type: item.adaptiveType,
              last_checked_at: item.lastCheckedAt,
              next_visit,
              type: item.type,
              isHealthy: item.isHealthy,
              adaptiveType: item.adaptiveType,
              health,
              domain: item.domain,
            };
          })
      : [];

    const csvData = [
      columns,
      ...visibleItems.map((item) => {
        return [
          item.enabled ? 'Enabled' : 'Disabled',
          item.sitemap_url,
          item.created_at,
          item.interval,
          item.device_type,
          item.last_checked_at,
          item.next_visit,
          SITEMAP_ORIGIN_LABELS[getSitemapOrigin(item.type)] ?? 'Auto-discovered',
          item.health,
        ];
      }),
    ];

    return [visibleItems, csvData];
  }, [getSitemapsData]);

  const isLoading = isFetching || prerenderUser.inProgress || isDeleteSitemapLoading || isUpdatingSitemap;

  const sitemapsOnPage = getSitemapsData?.data.length ?? 0;
  const hasNextPage = getSitemapsData ? page < getSitemapsData.pages : false;
  return (
    <AdminTemplate metaTags={META_TAGS}>
      <UpgradePlanProvider>
        <ImportSitemapModal
          open={isImportModalVisible}
          errorMsg={isAddNewSitemapError || isReCacheSitemapError ? 'Failed to import sitemap' : null}
          onClose={closeImportModal}
          onImport={onImport}
          confirmLoading={isAddNewSitemapLoading}
          mobileOptimizedEnabled={prerenderUser.mobileAdaptive}
        />

        {isError && <ErrorAlert modal={false} content={{ text: 'Failed to load sitemaps' }} />}

        <Flex vertical style={{ marginBottom: 24 }}>
          <div>
            <Title level={2}>Sitemaps</Title>
            <Text type="secondary">
              Increase your SEO reactivity and reduce on-demand renderings by loading your sitemaps into Prerender. Our
              system will fetch and render those pages to prepare them for crawler requests.
            </Text>
          </div>
        </Flex>

        <BannerWithLink />

        <Container addClass={'p-0'} fluid>
          <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
            <p>You have {visibleItems.length} imported sitemaps in total</p>
            <Flex gap={8}>
              <ButtonWithRole
                disabledFor={[USER_ROLES.BILLING_MANAGER, USER_ROLES.GUEST]}
                type="primary"
                key="add"
                onClick={() => setIsImportModalVisible(true)}
              >
                Import Sitemap
              </ButtonWithRole>
              <ButtonWithRole
                disabledFor={[USER_ROLES.BILLING_MANAGER, USER_ROLES.GUEST]}
                key="enable-all"
                type="primary"
                danger={false}
                onClick={() => toggleAll(true)}
                disabled={!visibleItems.length || isToggleAllSitemapsLoading}
              >
                Activate All
              </ButtonWithRole>
              <ButtonWithRole
                disabledFor={[USER_ROLES.BILLING_MANAGER, USER_ROLES.GUEST]}
                key="disable-all"
                type="primary"
                danger={true}
                onClick={() => toggleAll(false)}
                disabled={!visibleItems.length || isToggleAllSitemapsLoading}
              >
                Deactivate All
              </ButtonWithRole>
            </Flex>
          </Flex>
          <Card
            title="Sitemaps"
            extra={[
              <Space key="space" wrap>
                <Tooltip key="reload-tooptip" title="Refresh sitemap list">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      refetch();
                    }}
                    disabled={isFetching}
                    style={{ marginRight: 8 }}
                  />
                </Tooltip>
                <CSVLink key="csv-export" data={csvData} filename={'sitemap.csv'}>
                  <Button
                    key="export"
                    disabled={!visibleItems.length || isToggleAllSitemapsLoading}
                    onClick={() => {
                      track('Sitemap List Engaged', {
                        subscription_plan: prerenderUser.plan.name,
                        smap_is_activated: false,
                        smap_is_deactivated: false,
                        smap_is_exported: true,
                        smap_is_deleted: false,
                        smap_num_activated: 0,
                        smap_num_deactivated: 0,
                      });
                    }}
                  >
                    Export
                  </Button>
                </CSVLink>
              </Space>,
            ]}
            styles={{ body: { padding: 12 } }}
          >
            <SitemapsListTable
              rows={visibleItems}
              isLoading={isLoading}
              onStatusToggle={sitemapToggled}
              onDeleteClick={onDeleteSitemap}
              mobileAdaptive={prerenderUser.mobileAdaptive}
              onDeviceUpdate={deviceTypeUpdated}
              onRevisitIntervaleUpdate={revisitIntervalUpdated}
            />
            {getSitemapsData && (sitemapsOnPage > 0 || page > 1) && (
              <PrevNextPagination
                page={page}
                pageSize={getSitemapsData.itemsPerPage}
                currentPageAmount={currentSitemapsData?.data.length ?? 0}
                onPrevClick={page > 1 ? () => setPage(page - 1) : undefined}
                onNextClick={hasNextPage ? () => setPage(page + 1) : undefined}
              />
            )}
          </Card>
        </Container>
      </UpgradePlanProvider>
    </AdminTemplate>
  );
}

export default requireAuth(SitemapListPage);
